import { useState, useMemo } from 'react';
import { CaretDown, Check, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * ログイン情報の共有先を選ぶ（社員 / 部署の複数選択）。
 * 値は id の配列。座席割当と同じ社員ID・部署IDを使うので、後から座席モデルと
 * 突き合わせられる（共有＝閲覧権限、座席＝利用者。別物として保持する）。
 */
export default function LicenseSharePicker({ label, options, selected, onChange, testId, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const picked = selected || [];
  const shown = useMemo(() => {
    const s = q.trim();
    return s ? options.filter((o) => (o.name || '').includes(s)) : options;
  }, [options, q]);
  const names = picked
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean);

  const toggle = (id) => onChange(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testId}
          className="h-10 w-full justify-between border-zinc-300 font-normal">
          <span className="truncate text-left" data-no-translate={names.length ? 'strict' : undefined}>
            {names.length ? `${label}: ${names.join('、')}` : (placeholder || label)}
          </span>
          <CaretDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-2">
        <div className="relative mb-2">
          <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="名前で絞り込み"
            data-testid={`${testId}-search`} className="h-8 pl-7 text-sm" />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {shown.length === 0 && <p className="py-4 text-center text-xs text-zinc-400">該当なし</p>}
          {shown.map((o) => (
            <button key={o.id} type="button" onClick={() => toggle(o.id)}
              data-testid={`${testId}-option-${o.id}`}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-zinc-50">
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${picked.includes(o.id) ? 'border-teal-700 bg-teal-700 text-white' : 'border-zinc-300'}`}>
                {picked.includes(o.id) && <Check size={11} weight="bold" />}
              </span>
              <span className="truncate" data-no-translate>{o.name}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
